import * as Sentry from '@sentry/nextjs';
import axios from 'axios';

import { PAGE_ID_HEADER } from '@/shared/constants/axios-constants';
import { ERROR_SEVERITY, type TErrorSeverity } from '@/shared/constants/sentry-constants';
import {
	isAbortedRequest,
	isExpectedRateLimit,
	shouldIgnoreRouteForSentry,
} from '@/shared/utils/axios';
import { normalizeEndpoint, redactAndTruncate } from '@/shared/utils/sentry-redaction';
import { tabStorage } from '@/shared/utils/tab-storage';

/** Which axios instance produced the error, so auth failures are filterable in Sentry. */
export type TApiErrorSource = 'app' | 'auth' | 'agent';

type TReportOptions = {
	errorType: string;
	statusCode: number | null;
	source: TApiErrorSource;
};

const get5xxCategory = (statusCode: number): string => {
	switch (statusCode) {
		case 500:
			return '5xx_internal_server_error';
		case 502:
			return '5xx_bad_gateway';
		case 503:
			return '5xx_service_unavailable';
		case 504:
			return '5xx_gateway_timeout';
		default:
			return '5xx_other';
	}
};

/**
 * Sentry drops anything past 200 characters in a tag value. Trim to fit ourselves so the
 * cut is visible in the tag instead of happening silently server-side.
 */
const MAX_TAG_LENGTH = 200;
const TAG_TRUNCATION_SUFFIX = '…';

const truncateForTag = (value: string): string =>
	value.length > MAX_TAG_LENGTH
		? `${value.slice(0, MAX_TAG_LENGTH - TAG_TRUNCATION_SUFFIX.length)}${TAG_TRUNCATION_SUFFIX}`
		: value;

/**
 * The signed-in user's email, already on the Sentry scope via `setSentryUser`.
 *
 * Read back from the scope rather than re-plumbed through the call sites: `setUser` lands
 * on the isolation scope (node) or the global scope (browser), and neither is visible from
 * the local scope inside `withScope`, so both are checked.
 */
const getScopeUserEmail = (): string | undefined => {
	try {
		return Sentry.getIsolationScope().getUser()?.email ?? Sentry.getGlobalScope().getUser()?.email;
	} catch {
		return undefined;
	}
};

const getStatusClass = (statusCode: number): string => {
	if (statusCode >= 500) return '5xx';
	if (statusCode >= 400) return '4xx';
	return `${Math.floor(statusCode / 100)}xx`;
};

/**
 * Build a message for a thrown non-Error. `String(value)` yields "[object Object]",
 * which collapses every such throw into one useless Sentry issue, so serialize the
 * payload instead — through the redactor, since it may carry credentials.
 */
const describeNonError = (error: unknown): string => {
	if (typeof error === 'string') return redactAndTruncate(error) ?? error;
	return redactAndTruncate(error) ?? String(error);
};

/**
 * Severity and category are one decision over the same status ladder — kept in a single
 * table so a new rule cannot be added to one and forgotten in the other.
 */
const classify = (
	statusCode: number | null,
	errorType: string
): { severity: TErrorSeverity; category: string } => {
	if (statusCode === null) {
		return {
			severity: ERROR_SEVERITY.HIGH,
			category: errorType === 'Network Error' ? 'network_error' : 'request_error',
		};
	}
	if (statusCode >= 500) {
		return { severity: ERROR_SEVERITY.CRITICAL, category: get5xxCategory(statusCode) };
	}
	// 404 is routine — a renamed resource, a stale link, a crawler on a public route.
	// Recorded for context, never at a level an alert rule should watch.
	if (statusCode === 404) return { severity: ERROR_SEVERITY.LOW, category: 'not_found' };
	if (statusCode === 401 || statusCode === 403) {
		return { severity: ERROR_SEVERITY.HIGH, category: 'auth_failure' };
	}
	return { severity: ERROR_SEVERITY.MEDIUM, category: 'client_error' };
};

/**
 * Report an API error to Sentry with full context.
 *
 * Every error reaches Sentry on its first occurrence — there is no threshold or
 * batching. The fingerprint below groups repeats into a single Sentry *issue*; it
 * does not reduce the number of events sent, so event volume tracks the raw API
 * error rate. Watch quota after any change that widens what is reported.
 *
 * Always captures an exception (never a message) so the issue keeps its stack trace.
 */
export function reportApiErrorToSentry(error: unknown, options: TReportOptions): void {
	try {
		const { errorType, statusCode, source } = options;

		const config = (
			error as {
				config?: {
					url?: string;
					method?: string;
					_retry?: boolean;
					baseURL?: string;
					params?: unknown;
					data?: unknown;
					headers?: Record<string, unknown>;
					metadata?: { startTime?: number };
				};
			}
		)?.config;

		const url = config?.url;

		if (shouldIgnoreRouteForSentry(url)) return;
		if (isAbortedRequest(error)) return;
		if (isExpectedRateLimit(error)) return;
		// Auth failures are expected churn (expired sessions, revoked access) — not error signal.
		if (statusCode === 401 || statusCode === 403) return;

		const method = config?.method?.toUpperCase();
		const { severity, category: errorCategory } = classify(statusCode, errorType);
		const endpoint = url ? normalizeEndpoint(url) : 'unknown';
		const workspaceId = tabStorage.get('selectedWorkspaceId');

		Sentry.withScope((scope) => {
			scope.setLevel(severity);

			scope.setTag('error_type', errorType);
			scope.setTag('error_source', source);
			scope.setTag('error_category', errorCategory);
			scope.setTag('is_authenticated', String(Boolean(workspaceId)));

			if (statusCode !== null) {
				scope.setTag('http_status', String(statusCode));
				scope.setTag('status_class', getStatusClass(statusCode));
				// Retained for existing Sentry saved searches and alert rules.
				scope.setTag('is_5xx', String(statusCode >= 500));
			}
			if (method) scope.setTag('http_method', method);
			if (url) scope.setTag('api_endpoint', endpoint);
			if (workspaceId) scope.setTag('workspace_id', String(workspaceId));
			// user.email already reaches Sentry, but alert rules can only interpolate tags,
			// so it is mirrored here to reach Slack alongside api_response_body.
			const userEmail = getScopeUserEmail();
			if (userEmail) scope.setTag('user_email', userEmail);
			if (config?._retry) scope.setTag('retry_failed', 'true');

			if (axios.isAxiosError(error)) {
				if (error.code) scope.setTag('axios_error_code', error.code);

				const responseData = error.response?.data as { error_type?: string } | undefined;
				if (responseData?.error_type) {
					scope.setTag('backend_error_type', String(responseData.error_type));
				}

				if (config) {
					const startTime = config.metadata?.startTime;
					scope.setContext('API Request', {
						url: config.url,
						baseURL: config.baseURL,
						method,
						params: redactAndTruncate(config.params),
						body: redactAndTruncate(config.data),
						// Whitelisted, never spread — Authorization and Cookie must not reach Sentry.
						headers: {
							'Content-Type': config.headers?.['Content-Type'],
							[PAGE_ID_HEADER]: config.headers?.[PAGE_ID_HEADER],
						},
						durationMs: typeof startTime === 'number' ? Date.now() - startTime : undefined,
					});
				}

				if (error.response) {
					const responseBody = redactAndTruncate(error.response.data);
					scope.setContext('API Response', {
						status: error.response.status,
						statusText: error.response.statusText,
						data: responseBody,
					});
					// Duplicated from the context above as a tag purely so the response body can
					// reach Slack: Sentry alert rules can interpolate tags, never contexts. Cut to
					// Sentry's own 200-char tag limit here rather than letting it truncate silently.
					if (responseBody) {
						scope.setTag('api_response_body', truncateForTag(responseBody));
					}
				}
			}

			if (typeof window !== 'undefined') {
				scope.setContext('Client State', {
					route: window.location.pathname,
					release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
					online: navigator.onLine,
					viewport: `${window.innerWidth}x${window.innerHeight}`,
				});
			}

			scope.setFingerprint([
				source,
				errorCategory,
				method ?? 'UNKNOWN',
				endpoint,
				String(statusCode ?? 'none'),
			]);

			Sentry.captureException(error instanceof Error ? error : new Error(describeNonError(error)));
		});
	} catch (reportingError) {
		// Enrichment failed. This module's whole job is making failures visible, so fall
		// back to a bare capture rather than letting the error vanish into the console.
		console.error('Failed to report API error to Sentry:', reportingError);
		try {
			Sentry.captureException(error instanceof Error ? error : new Error('API error'), {
				tags: { error_source: options.source, enrichment_failed: 'true' },
			});
		} catch {
			// Sentry itself is unavailable — nothing further we can do.
		}
	}
}
